import React, { CSSProperties, ReactNode, useState } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import styles from './SearchForApprovedContentsDialog.module.css';
import { ContentSortInput, Tag, useGetContentsWithCategoryQuery, useSetProjectApprovedContentsMutation } from '../../../generated/graphql';
import DataTableNew from '../../main/DataTableNew/DataTableNew';
import ImageViewerButton from '../../main/ImageViewerButton/ImageViewerButton';
import { createGraphQLOrder, TableSort } from '../../main/DataTableNew/DataTableSorting';

type SearchForApprovedContentsDialogProps = {
    onClose: () => void,
    projectId: string,
    libraryId: string,
    visible: boolean,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const SearchForApprovedContentsDialog = (props: SearchForApprovedContentsDialogProps) => {
    const { style, visible, libraryId, projectId, onClose } = props;
    const [search, setSearch] = useState("");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [sort, setSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });
    const contents = useGetContentsWithCategoryQuery({
        variables: {
            tagIds: tagIds,
            contains: search,
            category: "Views",
            libraryId: libraryId,
            order: createGraphQLOrder<ContentSortInput>(sort),
        }
    });
    const [setApprovedContents, setApprovedContentsResult] = useSetProjectApprovedContentsMutation();

    const handleOnOk = () => {
        setApprovedContents({
            variables: {
                id: projectId,
                ids: selectedIds
            }
        })
            .then(() => onClose())
            .catch(err => onClose());
    };

    const handleFilterChanged = (tags: Tag[]) => {
        tags && setTagIds(tags.map(t => t.id || ""));
    };

    return (
        <DialogBase
            title="Search Contents"
            type="OkCancel"
            isLoading={setApprovedContentsResult.loading}
            visible={visible}
            onClose={onClose}
            onOkClick={handleOnOk}
            className={styles.SearchForApprovedContentsDialog}
            style={style}
            data-testid="SearchForApprovedContentsDialog">
            <DataTableNew
                sortingMode="server"
                sort={sort}
                onSortChange={setSort}
                showTags
                showCheckboxes
                filterable
                style={{ height: "600px", width: "900px" }}
                onSelected={(ids) => setSelectedIds(ids)}
                onSearchChanged={(value) => setSearch(value)}
                onFilterChanged={handleFilterChanged}
                data={contents.data?.Library?.contents?.nodes}
                isLoading={contents.loading}
                columns={[{
                    id: '_preview',
                    name: '',
                    width: '55px',
                    render: (row) => row.thumbnail?.downloadUrl
                        ? <ImageViewerButton title={row.name} link={row.thumbnail.downloadUrl} />
                        : null,
                }]}
                fetchMore={() => contents.data?.Library?.contents?.pageInfo?.hasNextPage && contents.fetchMore({
                    variables: {
                        after: contents.data?.Library?.contents?.pageInfo.endCursor,
                        order: createGraphQLOrder<ContentSortInput>(sort),
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        if (!fetchMoreResult) return previousResult;
                        const previousContents = contents.data?.Library?.contents?.nodes || [];
                        const newContents = fetchMoreResult.Library?.contents?.nodes || [];
                        return {
                            ...previousResult,
                            Library: {
                                id: contents.data?.Library?.id,
                                contents: {
                                    nodes: [...previousContents, ...newContents],
                                    pageInfo: fetchMoreResult.Library?.contents?.pageInfo as any
                                }
                            }
                        };
                    }
                })}
            />
        </DialogBase>
    );
};

export default SearchForApprovedContentsDialog;
